import { useId } from 'react'

import { t, tOr } from '../i18n'
import { Overlay } from '../ui'
import { COLUMN, DEFAULT_LAYOUT, move, toggle, type CardKey, type Layout } from './dashboard'

/**
 * Turn the overview's cards on and off and move them, for this person.
 *
 * Every change applies at once, so what the panel says and what is behind it
 * never disagree; "Back to the default" undoes all of it.
 */
export function Customize({
  layout,
  offered,
  onChange,
  onClose,
}: {
  layout: Layout
  /** The cards this person may see on this project at all. */
  offered: readonly CardKey[]
  onChange: (next: Layout) => void
  onClose: () => void
}) {
  const titleId = useId()
  const columns: { key: 'main' | 'side'; label: string }[] = [
    { key: 'main', label: t('home.customize.main') },
    { key: 'side', label: t('home.customize.side') },
  ]

  return (
    <Overlay kind="dialog" titleId={titleId} onClose={onClose}>
      <header>
        <h2 id={titleId}>{t('home.customize.title')}</h2>
        <button type="button" className="subtle" onClick={onClose}>
          {t('app.close')}
        </button>
      </header>
      <div className="body">
        {columns.map((column) => {
          const cards = layout.order.filter(
            (card) => COLUMN[card] === column.key && offered.includes(card),
          )
          return (
            <fieldset key={column.key} className="customize">
              <legend>{column.label}</legend>
              <ol>
                {cards.map((card, index) => (
                  <li key={card}>
                    <label>
                      <input
                        type="checkbox"
                        checked={!layout.hidden.includes(card)}
                        onChange={() => onChange(toggle(layout, card))}
                      />{' '}
                      {tOr(`home.card.${card}`, card)}
                    </label>
                    <span className="customize-move">
                      <button
                        type="button"
                        className="subtle"
                        disabled={index === 0}
                        aria-label={t('home.customize.up', {
                          card: tOr(`home.card.${card}`, card),
                        })}
                        onClick={() => onChange(move(layout, card, -1, offered))}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="subtle"
                        disabled={index === cards.length - 1}
                        aria-label={t('home.customize.down', {
                          card: tOr(`home.card.${card}`, card),
                        })}
                        onClick={() => onChange(move(layout, card, 1, offered))}
                      >
                        ↓
                      </button>
                    </span>
                  </li>
                ))}
              </ol>
            </fieldset>
          )
        })}
        <p className="muted">{t('home.customize.kept')}</p>
        <button type="button" onClick={() => onChange(DEFAULT_LAYOUT)}>
          {t('home.customize.reset')}
        </button>
      </div>
    </Overlay>
  )
}
